import React, { useState, useEffect, Component, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectsAPI, reportsAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ArrowLeft, Download, Printer, CheckCircle, GripVertical, Info, Edit, Link2, AlertTriangle, Plus, Trash2, Mail
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { COLOR_SCHEMES, STYLE_PACKS, REPORT_SECTIONS } from '@/lib/constants';
import { format } from 'date-fns';
import { PDFViewer, PDFDownloadLink } from '@react-pdf/renderer';
import ReportPDF from '@/components/pdf/ReportPDF';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Footer from '@/components/Footer';
import { SafePDFViewer } from '@/components/SafeChart';
import EmailReportModal from '@/components/EmailReportModal';

// Error Boundary for PDF rendering
class PDFErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('PDF rendering error:', error, errorInfo);
  }

  // Removed componentWillUnmount setState to prevent memory leak warning

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full bg-slate-50 rounded-lg p-8">
          <AlertTriangle className="h-12 w-12 text-slate-400 mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">PDF Preview Unavailable</h3>
          <p className="text-sm text-slate-600 text-center mb-4">
            Unable to render preview. You can still export the PDF using the button above.
          </p>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            Try Again
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

const TEMPLATE_DEFAULTS = {
  standard: {
    status: true,
    progress: true,
    timeline: true,
    metrics: true,
    chart: true,
    accomplishments: true,
    upcoming: true,
    blockers: true,
    risks: true,
    decisions: true,
    kudos: true,
    milestones: false,
    budget: false,
    scope: false,
    notes: false,
  },
  executive: {
    status: true,
    progress: false,
    timeline: false,
    metrics: true,
    chart: false,
    accomplishments: true,
    upcoming: false,
    blockers: true,
    risks: true,
    decisions: true,
    kudos: false,
    milestones: false,
    budget: false,
    scope: false,
    notes: false,
  },
  compact: {
    status: true,
    progress: true,
    timeline: true,
    metrics: true,
    chart: true,
    accomplishments: true,
    upcoming: true,
    blockers: true,
    risks: true,
    decisions: true,
    kudos: true,
    milestones: true,
    budget: true,
    scope: true,
    notes: true,
  },
  visual: {
    status: false,
    progress: false,
    timeline: true,
    metrics: true,
    chart: true,
    accomplishments: true,
    upcoming: true,
    blockers: true,
    risks: false,
    decisions: false,
    kudos: false,
    milestones: false,
    budget: false,
    scope: false,
    notes: false,
  },
};

const ReportView = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [project, setProject] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMounted, setIsMounted] = useState(false);
  const [pdfReady, setPdfReady] = useState(false);
  const pdfContainerRef = useRef(null);

  // Customization state
  const [colorScheme, setColorScheme] = useState('slate');
  const [stylePack, setStylePack] = useState('modern');
  const [sectionOrder, setSectionOrder] = useState([]);
  const [debouncedSectionOrder, setDebouncedSectionOrder] = useState([]); // Debounced for PDF rendering
  const [sectionVisibility, setSectionVisibility] = useState(TEMPLATE_DEFAULTS.standard);
  const [template, setTemplate] = useState('standard');
  const [linkCopied, setLinkCopied] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [riskFilter, setRiskFilter] = useState('active');
  
  // Timeline view for PDF (3_month, 6_month, 12_month)
  const [timelineView, setTimelineView] = useState('6_month');
  
  // Custom sections state
  const [customSections, setCustomSections] = useState([]);
  const [debouncedCustomSections, setDebouncedCustomSections] = useState([]); // Debounced for PDF
  const [showCustomSections, setShowCustomSections] = useState(false);

  // Track mount state to prevent updates after unmount
  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
      setPdfReady(false);
      setReport(null);  // Clear data on unmount to force fresh load
      setProject(null);
    };
  }, []);

  // Delay PDF rendering until container has valid dimensions (prevents crash on back navigation)
  useEffect(() => {
    if (!isMounted || !report || !project || loading) {
      setPdfReady(false);
      return;
    }
    
    const checkAndSetReady = () => {
      const container = pdfContainerRef.current;
      if (container) {
        const rect = container.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          setPdfReady(true);
          return true;
        }
      }
      return false;
    };

    // Try immediately
    if (checkAndSetReady()) return;

    // Use requestAnimationFrame + timeout for layout completion
    const timeout = setTimeout(() => {
      requestAnimationFrame(() => {
        if (isMounted) setPdfReady(true);
      });
    }, 150);

    return () => clearTimeout(timeout);
  }, [isMounted, report, project, loading]);

  // Debounce sectionOrder updates to prevent rapid PDF re-renders during drag operations
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSectionOrder(sectionOrder);
    }, 300); // Wait 300ms after drag ends before updating PDF
    return () => clearTimeout(timer);
  }, [sectionOrder]);

  // Debounce customSections updates and auto-save (BUG 4 fix)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCustomSections(customSections);
    }, 300);
    return () => clearTimeout(timer);
  }, [customSections]);

  // Auto-save custom sections to backend when they change (BUG 4 fix)
  useEffect(() => {
    const saveCustomSections = async () => {
      if (!reportId || customSections.length === 0) return;
      // Only save if there are custom sections with content
      const hasContent = customSections.some(s => s.name || s.content);
      if (!hasContent) return;
      
      try {
        await reportsAPI.updateCustomSections(reportId, customSections);
      } catch (err) {
        console.error('Failed to save custom sections:', err);
      }
    };
    
    const timer = setTimeout(saveCustomSections, 1000); // Debounce 1s
    return () => clearTimeout(timer);
  }, [customSections, reportId]);

  // Share URL handler - copies public share URL with token for security
  const handleShareUrl = async () => {
    // Include the project's public token for secure access
    const token = project?.public_token;
    const publicUrl = token 
      ? `${window.location.origin}/shared/reports/${reportId}?token=${token}`
      : `${window.location.origin}/shared/reports/${reportId}`;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
      // Fallback: show the URL in an alert for manual copying
      alert(`Copy this URL: ${publicUrl}`);
    }
  };

  // Template change handler - updates visibility defaults
  const handleTemplateChange = (newTemplate) => {
    setTemplate(newTemplate);
    const defaults = TEMPLATE_DEFAULTS[newTemplate];
    if (defaults) {
      setSectionVisibility(defaults);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Guard: Don't fetch if reportId is undefined (can happen during back navigation)
  useEffect(() => {
    if (!reportId) return;
    loadReport();
  }, [reportId]);

  const loadReport = async () => {
    try {
      const reportRes = await reportsAPI.getOne(reportId);
      setReport(reportRes.data);

      const projectRes = await projectsAPI.getOne(reportRes.data.project_id);
      const currentProject = projectRes.data;
      
      // BUG 3 FIX: Use snapshot data for historical accuracy when viewing past reports
      // If the report has snapshot data, use it for risks/milestones to show historical state
      const reportData = reportRes.data;
      let projectForDisplay = { ...currentProject };
      
      if (reportData.project_snapshot) {
        // Merge snapshot data with current project settings
        projectForDisplay = {
          ...currentProject,
          // Use snapshot for historical data
          name: reportData.project_snapshot.name || currentProject.name,
          pm_name: reportData.project_snapshot.pm_name || currentProject.pm_name,
          sponsor_name: reportData.project_snapshot.sponsor_name || currentProject.sponsor_name,
          bac: reportData.project_snapshot.bac ?? currentProject.bac,
          scope_summary: reportData.project_snapshot.scope_summary || currentProject.scope_summary,
          evm_enabled: reportData.project_snapshot.evm_enabled ?? currentProject.evm_enabled,
        };
      }
      
      // Use risks and milestones snapshots if available
      if (reportData.risks_snapshot) {
        projectForDisplay.risks = reportData.risks_snapshot;
      }
      if (reportData.milestones_snapshot) {
        projectForDisplay.milestones = reportData.milestones_snapshot;
      }
      
      setProject(projectForDisplay);

      const chartRes = await projectsAPI.getReportData(reportRes.data.project_id);
      setChartData(chartRes.data);

      // Bug 4 fix: Ensure defaults for color scheme and style pack (use current settings)
      setColorScheme(currentProject.color_scheme || 'slate');
      setStylePack(currentProject.style_pack || 'modern');
      
      // PERMANENT FIX: Reconcile stored section order with REPORT_SECTIONS defaults
      // This ensures new sections are inserted in their correct positions, not appended to the end
      const storedOrder = currentProject.section_order || [];
      const defaultOrder = REPORT_SECTIONS.map(s => s.id);
      
      let finalOrder;
      if (!storedOrder || storedOrder.length === 0) {
        // No stored order - use defaults
        finalOrder = defaultOrder;
      } else {
        // Reconcile: insert missing sections at their correct positions
        // Start with stored order, then insert any missing sections where they belong
        finalOrder = [...storedOrder];
        
        // Find sections that exist in defaults but not in stored order
        const missingSections = defaultOrder.filter(id => !storedOrder.includes(id));
        
        // Insert each missing section at its correct position based on default order
        for (const missingId of missingSections) {
          // Double-check it's not already in finalOrder (prevent duplicates)
          if (finalOrder.includes(missingId)) continue;
          
          const defaultIndex = defaultOrder.indexOf(missingId);
          
          // Find the best insertion point by looking at surrounding sections in default order
          let insertIndex = finalOrder.length; // Default to end
          
          // Look for the section that comes BEFORE this one in default order
          for (let i = defaultIndex - 1; i >= 0; i--) {
            const prevSection = defaultOrder[i];
            const prevIndexInFinal = finalOrder.indexOf(prevSection);
            if (prevIndexInFinal !== -1) {
              insertIndex = prevIndexInFinal + 1;
              break;
            }
          }
          
          // If we didn't find a preceding section, look for what comes AFTER
          if (insertIndex === finalOrder.length) {
            for (let i = defaultIndex + 1; i < defaultOrder.length; i++) {
              const nextSection = defaultOrder[i];
              const nextIndexInFinal = finalOrder.indexOf(nextSection);
              if (nextIndexInFinal !== -1) {
                insertIndex = nextIndexInFinal;
                break;
              }
            }
          }
          
          // Insert at the determined position
          finalOrder.splice(insertIndex, 0, missingId);
        }
        
        // Remove duplicates while preserving order, then filter to valid sections only
        finalOrder = [...new Set(finalOrder)].filter(id => defaultOrder.includes(id));
      }
      
      setSectionOrder(finalOrder);
      
      // Ensure header is always visible with proper defaults
      const defaultVisibility = REPORT_SECTIONS.reduce((acc, s) => ({ ...acc, [s.id]: s.defaultOn }), {});
      const visibility = { ...defaultVisibility, ...(currentProject.section_visibility || {}) };
      visibility['header'] = true; // Force header to always be on
      
      // AUTO-TOGGLE: Enable sections when content exists
      const report = reportRes.data;
      
      // Kudos - check for non-empty array with actual content
      if (report.kudos && Array.isArray(report.kudos) && report.kudos.length > 0) {
        const hasValidKudos = report.kudos.some(k => k && (k.person || k.reason));
        if (hasValidKudos) {
          visibility['kudos'] = true;
        }
      } else if (report.kudos && typeof report.kudos === 'string' && report.kudos.trim()) {
        visibility['kudos'] = true;
      }
      
      if (report.risk_updates && report.risk_updates.length > 0) {
        visibility['risks'] = true;
      }
      if (report.milestone_updates && report.milestone_updates.length > 0) {
        visibility['milestones'] = true;
      }
      if (report.notes && report.notes.trim()) {
        visibility['notes'] = true;
      }
      if (report.accomplishments && report.accomplishments.length > 0) {
        visibility['accomplishments'] = true;
      }
      if (report.upcoming && report.upcoming.length > 0) {
        visibility['upcoming'] = true;
      }
      if (report.blockers && report.blockers.length > 0) {
        visibility['blockers'] = true;
      }
      if (report.decisions && report.decisions.length > 0) {
        visibility['decisions'] = true;
      }
      if (report.attachments && report.attachments.length > 0) {
        visibility['attachments'] = true;
      }
      
      // BUG 4 FIX: Load saved custom sections from report
      if (reportData.custom_sections && reportData.custom_sections.length > 0) {
        setCustomSections(reportData.custom_sections);
        setShowCustomSections(true);
      }
      
      setSectionVisibility(visibility);
    } catch (error) {
      console.error('Failed to load report:', error);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = sectionOrder.indexOf(active.id);
      const newIndex = sectionOrder.indexOf(over.id);
      const newOrder = arrayMove(sectionOrder, oldIndex, newIndex);
      
      // Verify no duplicates introduced
      const hasDuplicates = newOrder.length !== new Set(newOrder).size;
      if (hasDuplicates) {
        console.error('Duplicate detected in section order:', newOrder);
        return; // Don't apply corrupted order
      }
      
      setSectionOrder(newOrder);

      // Persist section order to backend
      try {
        await projectsAPI.update(project.id, { section_order: newOrder });
      } catch (error) {
        console.error('Failed to save section order:', error);
        // Revert on error
        setSectionOrder(sectionOrder);
      }
    }
  };

  const toggleSection = async (sectionId) => {
    // Don't allow toggling header
    if (sectionId === 'header') return;
    const newVisibility = { ...sectionVisibility, [sectionId]: !sectionVisibility[sectionId] };
    setSectionVisibility(newVisibility);
    
    // Persist section visibility to backend
    try {
      await projectsAPI.update(project.id, { section_visibility: newVisibility });
    } catch (error) {
      console.error('Failed to save section visibility:', error);
      // Revert on error
      setSectionVisibility(sectionVisibility);
    }
  };

  // Custom section handlers
  const addCustomSection = () => {
    setCustomSections(prev => [...prev, { id: `custom-${Date.now()}`, name: '', content: '' }]);
  };

  const updateCustomSection = (id, field, value) => {
    setCustomSections(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const removeCustomSection = (id) => {
    setCustomSections(prev => prev.filter(s => s.id !== id));
  };

  // Guard against missing reportId (can happen briefly on back navigation)
  if (!reportId) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-500">Redirecting...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-8">
        <div className="container mx-auto max-w-6xl">
          <Skeleton className="h-8 w-48 mb-4" />
          <Skeleton className="h-[600px] w-full" />
        </div>
      </div>
    );
  }

  if (!report || !project) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-500 mb-4">Report not found or still loading...</p>
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const scheme = COLOR_SCHEMES[colorScheme];
  const style = STYLE_PACKS[stylePack];
  const isPredictive = project.methodology === 'predictive';

  // Filter out header from the sections list for the sidebar (since it can't be toggled)
  const toggleableSections = sectionOrder.filter(id => id !== 'header');

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-slate-100">
        {/* Header */}
        <header className="border-b bg-white sticky top-0 z-50 no-print">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(`/projects/${project.id}`)}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-lg font-bold">Report View</h1>
                <p className="text-sm text-slate-600">{project.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => navigate(`/projects/${project.id}/reports/new?edit=${reportId}`)} data-testid="edit-report-btn">
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Button variant="outline" onClick={() => navigate('/dashboard')} data-testid="dashboard-btn">
                Dashboard
              </Button>
              <Button variant="outline" onClick={() => setShowEmailModal(true)} data-testid="email-report-btn">
                <Mail className="h-4 w-4 mr-2" />
                Email
              </Button>
              <Button variant="outline" onClick={handleShareUrl} data-testid="share-url-btn">
                {linkCopied ? (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Link2 className="h-4 w-4 mr-2" />
                    Share URL
                  </>
                )}
              </Button>
              <PDFDownloadLink
                key={sectionOrder.join(',') + customSections.map(s => s.id).join(',') + timelineView}
                document={
                  <ReportPDF
                    report={report}
                    project={project}
                    chartData={chartData}
                    colorScheme={colorScheme}
                    stylePack={stylePack}
                    sectionOrder={sectionOrder}
                    sectionVisibility={sectionVisibility}
                    template={template}
                    riskFilter={riskFilter}
                    timelineView={timelineView}
                    customSections={showCustomSections ? customSections : []}
                  />
                }
                fileName={`${project.name.replace(/\s+/g, '_')}_Report_${format(new Date(report.report_date), 'yyyy-MM-dd')}.pdf`}
              >
                {({ loading: pdfLoading }) => (
                  <Button disabled={pdfLoading} data-testid="export-pdf-btn">
                    <Download className="h-4 w-4 mr-2" />
                    {pdfLoading ? 'Generating...' : 'Export PDF'}
                  </Button>
                )}
              </PDFDownloadLink>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-4 py-8 max-w-6xl">
          <div className="grid lg:grid-cols-4 gap-6">
            {/* Customization Sidebar */}
            <div className="lg:col-span-1 space-y-4 no-print">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Color Scheme</CardTitle>
                </CardHeader>
                <CardContent>
                  <Select value={colorScheme} onValueChange={setColorScheme}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(COLOR_SCHEMES).map(([key, val]) => (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded" style={{ backgroundColor: val.primary }} />
                            {val.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Template</CardTitle>
                </CardHeader>
                <CardContent>
                  <Select value={template} onValueChange={handleTemplateChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="executive">Executive</SelectItem>
                      <SelectItem value="compact">Compact</SelectItem>
                      <SelectItem value="visual">Visual</SelectItem>
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-sm">Sections</CardTitle>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-slate-400" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Drag to reorder sections. Toggle to show/hide.</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </CardHeader>
                <CardContent>
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={toggleableSections} strategy={verticalListSortingStrategy}>
                      <div className="space-y-2">
                        {toggleableSections.map((sectionId) => {
                          const section = REPORT_SECTIONS.find(s => s.id === sectionId);
                          if (!section) return null;
                          return (
                            <SortableItem
                              key={sectionId}
                              id={sectionId}
                              section={section}
                              visible={sectionVisibility[sectionId]}
                              onToggle={() => toggleSection(sectionId)}
                              riskFilter={riskFilter}
                              setRiskFilter={setRiskFilter}
                              timelineView={timelineView}
                              setTimelineView={setTimelineView}
                            />
                          );
                        })}
                      </div>
                    </SortableContext>
                  </DndContext>
                  
                  {/* Custom Sections */}
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Custom Sections</span>
                      </div>
                      <Switch 
                        checked={showCustomSections} 
                        onCheckedChange={setShowCustomSections}
                        className="data-[state=checked]:bg-blue-600"
                      />
                    </div>
                    
                    {showCustomSections && (
                      <div className="space-y-3">
                        {customSections.map((section, index) => (
                          <div key={section.id} className="p-3 bg-slate-50 rounded-lg space-y-2">
                            <div className="flex items-center justify-between">
                              <Label className="text-xs text-slate-500">Section {index + 1}</Label>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-6 w-6 p-0 text-slate-400 hover:text-red-500"
                                onClick={() => removeCustomSection(section.id)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                            <Input
                              placeholder="Section Name (Heading)"
                              value={section.name}
                              onChange={(e) => updateCustomSection(section.id, 'name', e.target.value)}
                              className="h-8 text-sm"
                            />
                            <Textarea
                              placeholder="Section content..."
                              value={section.content}
                              onChange={(e) => updateCustomSection(section.id, 'content', e.target.value)}
                              className="text-sm min-h-[60px]"
                            />
                          </div>
                        ))}
                        
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="w-full"
                          onClick={addCustomSection}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add Custom Section
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

            </div>

            {/* Report Preview - Using PDFViewer */}
            <div className="lg:col-span-3">
              <div 
                ref={pdfContainerRef}
                className="bg-white rounded-lg shadow-lg overflow-hidden" 
                style={{ height: 'calc(100vh - 160px)', minHeight: '700px' }}
              >
                <PDFErrorBoundary>
                  {pdfReady && report && project && debouncedSectionOrder.length > 0 ? (
                    <SafePDFViewer 
                      key={`pdf-${reportId}-${debouncedSectionOrder.join(',')}-${JSON.stringify(sectionVisibility)}-${timelineView}`}
                      PDFViewerComponent={PDFViewer}
                      width="100%" 
                      height="100%" 
                      showToolbar={false}
                    >
                      <ReportPDF
                        report={report}
                        project={project}
                        chartData={chartData}
                        colorScheme={colorScheme}
                        stylePack={stylePack}
                        sectionOrder={debouncedSectionOrder}
                        sectionVisibility={sectionVisibility}
                        template={template}
                        riskFilter={riskFilter}
                        timelineView={timelineView}
                        customSections={showCustomSections ? debouncedCustomSections : []}
                      />
                    </SafePDFViewer>
                  ) : (
                    <div className="flex items-center justify-center h-full bg-slate-50">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600 mx-auto mb-3"></div>
                        <p className="text-sm text-slate-500">Preparing preview...</p>
                      </div>
                    </div>
                  )}
                </PDFErrorBoundary>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Email Report Modal */}
      <EmailReportModal
        open={showEmailModal}
        onOpenChange={setShowEmailModal}
        report={report}
        project={project}
        onCreateList={() => {
          setShowEmailModal(false);
          navigate(`/projects/${project?.id}?tab=distribution`);
        }}
        userEmailSettings={user?.email_settings}
      />

      <Footer />
    </TooltipProvider>
  );
};

const SortableItem = ({ id, section, visible, onToggle, riskFilter, setRiskFilter, timelineView, setTimelineView }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

  const itemStyle = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div ref={setNodeRef} style={itemStyle} className="p-2 bg-slate-50 rounded">
      <div className="flex items-center gap-2">
        <button {...attributes} {...listeners} className="cursor-grab hover:bg-slate-200 rounded p-1">
          <GripVertical className="h-4 w-4 text-slate-400" />
        </button>
        <span className="flex-1 text-sm">{section.name}</span>
        <Switch 
          checked={visible} 
          onCheckedChange={onToggle}
          className="data-[state=checked]:bg-blue-600"
        />
      </div>
      {id === 'risks' && visible && (
        <div className="mt-2 ml-8">
          <Select value={riskFilter} onValueChange={setRiskFilter}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active Only</SelectItem>
              <SelectItem value="all">Include Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      {id === 'timeline' && visible && (
        <div className="mt-2 ml-8">
          <Select value={timelineView} onValueChange={setTimelineView}>
            <SelectTrigger className="h-8 text-xs" data-testid="timeline-view-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3_month">3 Month</SelectItem>
              <SelectItem value="6_month">6 Month</SelectItem>
              <SelectItem value="12_month">12 Month</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
};

export default ReportView;
